import { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Sidebar } from "../../components/Sidebar";
import { useAuthUser } from "../../auth/AuthContext";
import { useBadgeCounts } from "../../hooks/useBadgeCounts";
import { useUnreadCount } from "../../hooks/useUnreadCount";

const SECTION_TITLES: Record<string, string> = {
  "/dashboard/orders": "Orders",
  "/dashboard/samples": "Samples at Lab",
  "/dashboard/results": "Results Received",
  "/dashboard/reports-published": "Reports Published",
  "/dashboard/content/articles/create": "Create Article",
  "/dashboard/content/faqs": "FAQ",
  "/dashboard/content/articles": "Explore Articles",
  "/dashboard/content/decode": "Hiya Decode",
  "/dashboard/content/legal-pages": "Legal Pages",
  "/dashboard/content": "Content Management",
  "/dashboard/users": "Users",
  "/dashboard/notifications": "Notifications",
  "/dashboard/push-notifications": "Push Notifications",
};

const EDIT_ARTICLE_PATTERN = /^\/dashboard\/content\/articles\/[^/]+\/edit$/;

export function DashboardLayout() {
  const user = useAuthUser();
  const location = useLocation();
  const { counts, refresh } = useBadgeCounts();
  const { count: unreadNotifications, refresh: refreshUnread } = useUnreadCount();

  // Refresh badges on route change so markTabViewed takes effect immediately
  useEffect(() => {
    const timer = setTimeout(refresh, 500);
    return () => clearTimeout(timer);
  }, [location.pathname, refresh]);

  // Refresh unread badge on route change so marking notifications read takes
  // effect immediately when navigating away from /dashboard/notifications.
  useEffect(() => {
    const timer = setTimeout(refreshUnread, 500);
    return () => clearTimeout(timer);
  }, [location.pathname, refreshUnread]);

  const title = EDIT_ARTICLE_PATTERN.test(location.pathname)
    ? "Edit Article"
    : Object.entries(SECTION_TITLES).find(([path]) =>
        location.pathname.startsWith(path)
      )?.[1] ?? "Orders";

  return (
    <div className="flex min-h-screen">
      <Sidebar
        email={user?.email ?? ""}
        badgeCounts={counts}
        unreadNotifications={unreadNotifications}
      />
      <main className="flex-1 bg-bg-primary">
        <div className="h-[62px] border-b border-stroke-primary bg-bg-primary flex items-center px-6">
          <h1 className="text-lg font-semibold text-text-primary">{title}</h1>
        </div>
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
